import { Link } from "react-router";
import { useConfig } from "../components/Shell.tsx";

/** The seller, as Spanish law (LSSI-CE art. 10) requires it to be shown. */
const OWNER = {
  name: "José Ramón Pena Rodríguez",
  nif: "33994845V",
  address: "Celso Emilio Ferreiro 43, 6D, 32004 Ourense, Spain",
};
const UPDATED = "30 October 2026";

function Contact() {
  const cfg = useConfig();
  const email = cfg.data?.supportEmail ?? "support@memeup.store";
  return <a href={`mailto:${email}`}>{email}</a>;
}

export function LegalNoticePage() {
  return (
    <div className="sheet sheet--narrow prose">
      <h1 className="page-title">Legal notice</h1>
      <p className="muted">Last updated {UPDATED}</p>
      <h2>Who runs memeup</h2>
      <p>
        memeup.store is owned and run by {OWNER.name}, a sole trader in Spain.
        <br />
        Tax ID (NIF): {OWNER.nif}
        <br />
        Address: {OWNER.address}
        <br />
        Email: <Contact />
      </p>
      <h2>Using this website</h2>
      <p>
        You can browse the shop and make designs freely. Buying is governed by our{" "}
        <Link to="/terms">terms of sale</Link>, and your personal data by our{" "}
        <Link to="/privacy">privacy policy</Link> and <Link to="/cookies">cookie policy</Link>.
      </p>
      <h2>Content and trademarks</h2>
      <p>
        The memeup name, logo, site design and our own artwork belong to {OWNER.name}. Meme
        templates are memeup originals or used as parody and commentary. If you believe something
        here infringes your rights, email us and we'll review it quickly.
      </p>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <div className="sheet sheet--narrow prose">
      <h1 className="page-title">Privacy policy</h1>
      <p className="muted">Last updated {UPDATED}</p>
      <h2>Who is responsible</h2>
      <p>
        The data controller is {OWNER.name}, NIF {OWNER.nif}, {OWNER.address}. For anything about
        your data, email <Contact />.
      </p>

      <h2>What we collect and why</h2>
      <ul>
        <li>
          <strong>Orders:</strong> your name, email, shipping address and, if you give it, phone
          number, plus what you bought. We need these to make, ship and support your order, and to
          keep the records tax law requires. Legal basis: performing our contract with you, and our
          legal obligations.
        </li>
        <li>
          <strong>Sign-in:</strong> your email address, to send you sign-in links and show your
          orders and designs. Legal basis: performing our contract with you.
        </li>
        <li>
          <strong>Designs and uploads:</strong> the images and text you use in the Studio, so we can
          print them and keep them for you. Legal basis: performing our contract with you.
        </li>
        <li>
          <strong>Payment:</strong> PayPal handles your payment. We never see your card details; we
          receive the payment status and PayPal's transaction reference.
        </li>
        <li>
          <strong>Ad measurement:</strong> only if you choose "Accept all" cookies, the Meta Pixel
          tells us which Instagram and Facebook ads lead to visits and sales. Legal basis: your
          consent, which you can withdraw at any time in the{" "}
          <Link to="/cookies">cookie settings</Link>.
        </li>
      </ul>
      <p>We don't sell your data, and we don't send marketing emails without your consent.</p>

      <h2>Who we share it with</h2>
      <p>Only the companies that help us run the shop, each for its own part:</p>
      <ul>
        <li>Gelato (Norway) and its print partners: printing and shipping your order.</li>
        <li>PayPal (Europe) S.à r.l. et Cie, S.C.A. (Luxembourg): payments.</li>
        <li>Contabo GmbH (Germany): the servers that host the shop.</li>
        <li>Resend (USA): sending order and sign-in emails.</li>
        <li>Meta Platforms Ireland: ad measurement, only with your consent.</li>
      </ul>
      <p>
        Where data leaves the European Economic Area, as with Resend or when an order ships outside
        Europe, the transfer is protected by the EU–US Data Privacy Framework or the European
        Commission's standard contractual clauses.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Order records: as long as tax and commercial law requires, usually six years. Your account
        and designs: until you ask us to delete them. Sign-in links expire within minutes.
      </p>

      <h2>Your rights</h2>
      <p>
        You can ask to access, correct, delete or export your data, to restrict or object to how we
        use it, and withdraw consent at any time. Email <Contact /> and we'll answer within a month.
        If you're not satisfied, you can complain to the Spanish Data Protection Agency (
        <a href="https://www.aepd.es" rel="noreferrer">
          aepd.es
        </a>
        ).
      </p>
    </div>
  );
}

export function TermsPage() {
  return (
    <div className="sheet sheet--narrow prose">
      <h1 className="page-title">Terms of sale</h1>
      <p className="muted">Last updated {UPDATED}</p>
      <p>
        These terms apply to everything you buy on memeup.store. The seller is {OWNER.name}, NIF{" "}
        {OWNER.nif}, {OWNER.address}, <Contact />.
      </p>

      <h2>Products</h2>
      <p>
        Every item is printed for you after you order. There are two kinds: shop designs, from our
        catalog, and your own designs, made in the Studio from a template or your own image. Colours
        on screen can differ slightly from the printed result.
      </p>

      <h2>Prices and payment</h2>
      <p>
        Prices are in euros and include VAT where applicable. Shipping is calculated for your
        address and shown before you pay. You pay with PayPal or card through PayPal at checkout.
        Orders shipped outside the EU may have to pay import duties on delivery.
      </p>

      <h2>Your order</h2>
      <p>
        Your contract is made when we confirm your order by email after payment. We may cancel and
        fully refund an order we can't fulfil, for example if an item is no longer available or a
        design breaks the rules below.
      </p>

      <h2>Delivery</h2>
      <p>
        Printing takes a few business days, then your order ships with the option you chose. The
        delivery estimate is shown at checkout, and we email you a tracking link when it ships.
      </p>

      <h2>Returns: shop designs</h2>
      <p>
        You can withdraw from the purchase of shop designs within 14 days of receiving them, without
        giving a reason. Tell us by email at <Contact /> with your order number, then send the
        unworn items back to {OWNER.address} within 14 days. You pay the cost of returning them. We
        refund what you paid, including standard shipping, within 14 days of your message, once
        we've received the items back or proof that you sent them.
      </p>

      <h2>Returns: your own designs</h2>
      <p>
        Items made from your own Studio designs are made to your specifications, so the right of
        withdrawal does not apply to them (Spanish consumer law, article 103 c of the TRLGDCU). We
        can't take these back for change of mind or wrong size. Please check your size before
        ordering.
      </p>

      <h2>Damaged or faulty items</h2>
      <p>
        If anything arrives damaged, misprinted or faulty, email us a photo and your order number
        and we'll reprint it or refund you, at no cost to you. This is in addition to your legal
        guarantee of conformity, which lasts three years from delivery.
      </p>

      <h2>Your designs and uploads</h2>
      <p>
        Only upload images and text you have the right to use. You keep the rights to your content;
        you allow us to print it for your order and to show it on the share page if you choose to
        share it. We may refuse designs that infringe others' rights or are hateful, sexually
        explicit or illegal, and refund any order affected.
      </p>

      <h2>Law and disputes</h2>
      <p>
        These terms are governed by Spanish law. If you're a consumer, you keep the protection of
        the law of the country where you live, and you can bring a claim in the courts of your home
        town. If something goes wrong, email us first: we'd rather fix it.
      </p>
    </div>
  );
}
